import { Link } from "react-router-dom";
import { useInbox } from "../../../api-services/messaging/useInbox";
import { SITELINK } from "../../../app-routes/Links";
import { UserProfileInterface } from "../../../app-schemas/user.interface";
import { formatTimestampToRelativeTime, getUser, truncateText } from "../../../app-utils";
import AppLayout from "../../../layouts/AppLayout";
import CommunityTopBar from "../../community/CommunityTopBar";
import { useTheme } from "../../../context/theme-context";
import { Avatar } from "../../../assets/custom-icons";

// Skeleton Component for Inbox
const InboxSkeleton: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className="dark:bg-neutral-800 min-h-[82vh] mt-[0px]">
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 border-b last:border-b-0 animate-pulse ${
              isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex gap-2 w-full">
              {/* Avatar skeleton */}
              <div className={`size-10 shrink-0 rounded-full ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`} />
              
              <div className="flex-1">
                <div className="flex items-center justify-between w-full">
                  <div className="space-y-2">
                    {/* Name skeleton */}
                    <div className={`h-4 w-32 rounded ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    {/* Username skeleton */}
                    <div className={`h-3 w-20 rounded ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  {/* Time skeleton */}
                  <div className={`h-3 w-12 rounded ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`} />
                </div>
                {/* Message preview skeleton */}
                <div className={`mt-2 h-4 w-48 rounded ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Error Component
const InboxError: React.FC<{ onRetry: () => void; error: any }> = ({ onRetry, error }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] ${isDarkMode ? 'bg-neutral-800' : ''}`}>
      <div className="text-center p-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
          isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
        }`}>
          <svg className={`w-10 h-10 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Failed to load messages
        </h3>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {error?.message || 'Unable to connect to the chat server. Please check your connection.'}
        </p>
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

// Empty State Component
const InboxEmpty: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] ${isDarkMode ? 'bg-neutral-800' : ''}`}>
      <div className="text-center">
        <svg className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No messages yet
        </p>
        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Start a conversation to see it here
        </p>
        <Link
          to={SITELINK.HOME}
          className={`inline-block mt-6 px-4 py-2 text-sm rounded-lg transition-colors ${
            isDarkMode 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          Browse Communities
        </Link>
      </div>
    </div>
  );
};

const Inbox: React.FC<{ selectUser: (userId: number, name: string, image: string) => void;}> = ({ selectUser }) => {
  const { isDarkMode } = useTheme();
  const user: UserProfileInterface = getUser();
  const inbox = useInbox();

  const handleRetry = () => {
    inbox.refetch();
  };

  // Show skeleton while loading
  if (inbox.isLoading) {
    return (
      <AppLayout>
        <CommunityTopBar />
        <InboxSkeleton />
      </AppLayout>
    );
  }

  // Show error state
  if (inbox.isError) {
    return (
      <AppLayout>
        <CommunityTopBar />
        <InboxError onRetry={handleRetry} error={inbox.error} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <CommunityTopBar />
      <div className={`dark:bg-neutral-800 min-h-[82vh] mt-[0px]`}>
        <div className="space-y-2">
          {inbox.isFetched && inbox?.data?.length > 0 ? (
            inbox?.data?.map((item: any, index: number) => {
              const isCurrentUserReceiver = item.receiver?.id === user.id;
              const otherUser = isCurrentUserReceiver ? item.sender : item.receiver;
              
              return (
                <Link
                  to={`${SITELINK.CHATIN}`}
                  key={item.id || index}
                  className={`flex items-center justify-between p-3 border-b last:border-b-0 cursor-pointer w-full transition-colors ${
                    isDarkMode 
                      ? 'bg-neutral-800 hover:bg-neutral-700 border-gray-700' 
                      : 'bg-white hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => {
                    selectUser(
                      otherUser?.id,
                      otherUser?.username,
                      otherUser?.profile_picture_url
                    );
                  }}
                >
                  <div className="flex gap-2 w-full">
                    {/* Avatar */}
                    <div className="size-10 shrink-0">
                      <img
                        src={otherUser?.profile_picture_url || Avatar}
                        alt={otherUser?.username || 'User'}
                        className="size-10 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = Avatar;
                        }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className={`madimi-one text-sm truncate ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {otherUser?.fullname || otherUser?.username || 'User'}
                            </span>
                            <span className={`text-xs lowercase truncate ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              @{otherUser?.username || 'user'}
                            </span>
                          </div>
                        </div>
                        <div className="shrink-0">
                          <span className={`text-xs ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            {formatTimestampToRelativeTime(item.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Message preview with unread indicator */}
                      <div className="flex items-center gap-2 mt-1">
                        {!item.is_read && item.receiver?.id === user.id && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />
                        )}
                        <p className={`text-sm truncate ${
                          !item.is_read && item.receiver?.id === user.id
                            ? isDarkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'
                            : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {item.sender?.id === user.id && 'You: '}
                          {truncateText(item.message, 30)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          ) : (
            <InboxEmpty />
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Inbox;